import { inject, Injectable } from '@angular/core';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { RecommendedByDialogComponent } from '../../components/dialogs/recommended-by-dialog/recommended-by-dialog.component';
import { UserProfileDialogComponent } from '../../components/dialogs/user-profile-dialog/user-profile-dialog.component';
import { GuestDialogComponent } from '../../components/dialogs/guest-dialog/guest-dialog.component';
import { MessageDialogService } from './message-dialog.service';
import { MessageDialogData, RecommendedByDialogData, UserProfileDialogData } from '../../interfaces/dialog';
import { FeatureFlagService } from '../util/feature-flag.service';
import { MediaPlatformService, NavigationService } from '@outlaw/core';
import { AddPostComponent } from '../../components/add-post/add-post.component';

@Injectable({
  providedIn: 'root',
})
export class DialogManagerService {
  #dialogService = inject(DialogService);
  #messageDialogService = inject(MessageDialogService);
  #featureFlagService = inject(FeatureFlagService);
  #navigationService = inject(NavigationService);
  #mediaPlatformService = inject(MediaPlatformService);

  openRecommendedByDialog(options: RecommendedByDialogData): DialogRef {
    const maxWidth = this.#mediaPlatformService.isMobile() ? '360px' : '640px';

    const ref = this.#dialogService.open(RecommendedByDialogComponent, {
      data: options,
      maxWidth,
      width: '100%',
    });

    return ref;
  }

  openUserProfileDialog(options: UserProfileDialogData): DialogRef {
    const maxWidth = this.#mediaPlatformService.isMobile() ? '360px' : '640px';

    return this.#dialogService.open(UserProfileDialogComponent, {
      maxWidth,
      width: '100%',
      data: options,
    });
  }

  openMessageDialog(options: MessageDialogData): DialogRef {
    return this.#messageDialogService.open(options);
  }

  /**
   * Opens user profile either in a new window or dialog based on configuration
   * @param userUUID - User's UUID
   * @param username - User's username (required for new window navigation)
   * @param openInNewWindow - Override the default behavior (optional)
   * @returns DialogRef if opened as dialog, undefined if opened in new window
   */
  openUserProfile(userUUID: string, username?: string, openInNewWindow?: boolean): DialogRef | undefined {
    // Determine whether to open in new window
    const shouldOpenInNewWindow =
      openInNewWindow !== undefined ? openInNewWindow : this.#featureFlagService.isOpenProfileInNewWindowEnabled();

    if (shouldOpenInNewWindow && username) {
      // Open in new window
      const profileUrl = `/profile/${username}`;

      // Check if we should open in same window based on project config
      if (this.#featureFlagService.isOpenProfileInNewWindowEnabled()) {
        this.#navigationService.openOnSamePage(profileUrl);
      } else {
        this.#navigationService.openOnExternalPage(profileUrl);
      }

      return undefined;
    } else {
      // Open as dialog
      return this.openUserProfileDialog({ userUUID });
    }
  }

  openAddPostDialog(): DialogRef {
    const isMobile = this.#mediaPlatformService.isMobile();
    const maxWidth = isMobile ? '100%' : '640px';

    return this.#dialogService.open(AddPostComponent, {
      width: '100%',
      maxWidth,
      height: isMobile ? '100%' : undefined,
      windowClass: isMobile ? 'add-post-dialog add-post-dialog--fullscreen' : 'add-post-dialog',
      enableClose: true,
    });
  }

  openGuestLoginDialog(): DialogRef {
    return this.#dialogService.open(GuestDialogComponent, {
      enableClose: true,
    });
  }
}
